import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import * as dataSelectors from '@/data/data-selectors';
import {
  DataUnavailableWrapper,
  StyledDataUnavailableLight,
  StyledDataUnavailableStandard,
} from './all-charts-styles';

const DataUnavailableView = ({ light, placeholder }) => (
  <DataUnavailableWrapper>
    {placeholder || light ? (
      <StyledDataUnavailableLight />
    ) : (
      <StyledDataUnavailableStandard />
    )}
  </DataUnavailableWrapper>
);

function hasData(data) {
  // Looks at each point in a set and returns array of boolean values
  // Ensure that there are at least 3 points in a set before making it valid
  const hasEnoughDataInSets = data.datasets.map((set) => set.data.length >= 3);
  // Looks at each of the boolean values and determines if there is valid data to display
  const chartHasData = hasEnoughDataInSets.some((set) => set);
  return chartHasData;
}

/**
 * Higher order component responsible for taking a ChartTemplate and returning a
 * complete chart with data, styles, and configurations set.
 *
 * Important: We are connecting a higher order component to the store. This is possible
 * by having the higher order component return a JSX instance component. This is necessary
 * b/c redux's connect is expecting a component. ChartGenerator however is a function.
 * We need the result of ChartGenerator to be passed to connect. This is possible using
 * Redux "Compose".
 *
 * @param {*} WrappedComponent ChartTemplate
 * @param {*} componentProps Props passed from component in the file it wants to be used
 */
// eslint-disable-next-line react/display-name
const ChartGenerator = (WrappedComponent, componentProps) => (props) => {
  const { kpiData } = props; // props from mapStateToProps
  const {
    propertyData,
    dataGetter, // object that knows how to fetch the data for this chart
    options, // constructed chart options, also has plugin options
    plugins, // actual chart plugins
    barThickness, // bar thickness changes based on modal view or not
    width,
    height, // can dynamically change chart template container if needed
    // placeholder is when no data is available. May not be needed.
    placeholder,
    builderOptions,
  } = componentProps;

  const data = propertyData || kpiData;
  const chartData = dataGetter(data, barThickness);
  const chartHasData = hasData(chartData);

  if (!chartHasData) {
    return <DataUnavailableView light="light" placeholder={placeholder} />;
  }

  return (
    <WrappedComponent
      width={width}
      height={height}
      data={chartData}
      options={options}
      plugins={plugins}
      fadeInAnimation={componentProps?.fadeInAnimation}
      builderOptions={builderOptions}
    />
  );
};

const mapStateToProps = (state) => ({
  kpiData: dataSelectors.kpiData(state),
});

// https://medium.com/practo-engineering/connected-higher-order-component-hoc-93ee63c91526
const composedChartGenerator = compose(
  connect(mapStateToProps, null),
  ChartGenerator
);

export default composedChartGenerator;
